import { useEffect, useState } from "react";
import type { AgentDTO, SubjectTypeDTO, TriggerCondition, TriggerDTO, TriggerEventDTO, TriggerOutcome } from "../../shared/types";
import { del, get, patch, post } from "../lib/api";
import { useScopeTypes } from "../lib/scopes";
import { CopyBox } from "../pages/admin/ConnectorWays";
import { confirmDialog, Dialog } from "./Dialog";
import { List, ListRow } from "./List";
import { MoreOnScroll, usePaged } from "./MoreOnScroll";
import { Switch } from "./Switch";
import { TextLink } from "./TextLink";
import { Badge, Button, Choice, ErrorText, Field, FieldGroup, inputCls, timeAgo } from "./ui";

type Issued = { trigger: TriggerDTO; secret: string };
const OUTCOME_TONE: Record<TriggerOutcome, "emerald" | "zinc" | "amber" | "red"> = { started: "emerald", duplicate: "zinc", ignored: "zinc", refused: "amber", failed: "red" };

/** A condition's value as typed: true, false, null and numbers are themselves, anything else is text ("…" keeps "true" as text). */
function parseEquals(text: string): TriggerCondition["equals"] {
  const t = text.trim();
  if (/^(true|false|null|-?\d+(\.\d+)?|".*")$/.test(t)) return JSON.parse(t);
  return text;
}
const showEquals = (v: TriggerCondition["equals"]) => (typeof v === "string" && /^(true|false|null|-?\d+(\.\d+)?)$/.test(v) ? JSON.stringify(v) : String(v));

/**
 * The agent page's triggers tab (AIT-45): the addresses other apps send signed events to, each starting one run of the
 * agent on its own behalf. Any admin reads them and their events; the agent's owners create and change them.
 */
export function AgentTriggers({ agent }: { agent: AgentDTO }) {
  const { types } = useScopeTypes();
  const [rows, setRows] = useState<TriggerDTO[] | null>(null);
  const [open, setOpen] = useState<TriggerDTO | "new" | null>(null);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [error, setError] = useState<string | null>(null);
  const owns = agent.youAnswerFor === true;
  const type = (types ?? []).find((t) => agent.scopeTypes.includes(t.key)) ?? null;
  const load = () => get<TriggerDTO[]>(`/api/agents/${agent.id}/triggers`).then(setRows, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [agent.id]);

  const toggle = (t: TriggerDTO, enabled: boolean) => patch(`/api/triggers/${t.id}`, { enabled }).then(load, (e) => setError((e as Error).message));
  return (
    <>
      <List
        title={rows ? `Triggers · ${rows.length}` : "Triggers"}
        hint={`Another app sends a signed event to a trigger's address. Each event that matches starts one run of ${agent.name} on its own behalf, with the trigger's prompt and the event as material.`}
        action={owns && <Button onClick={() => setOpen("new")}>New trigger</Button>}
        empty={rows?.length === 0 ? `No trigger yet, so no other app can wake ${agent.name}.${owns ? "" : " Its owners add them."}` : undefined}
        footer={error && <ErrorText error={error} />}
      >
        {(rows ?? []).map((t) => (
          <ListRow
            key={t.id}
            title={t.name}
            subtitle={`${t.eventTypes.join(", ")} · ${t.scopeRule ? `the ${t.scopeRule.type} at ${t.scopeRule.path} in ${t.scopeRule.tool}` : "no scope"}`}
            onClick={() => setOpen(t)}
            trailing={
              <div className="flex items-center gap-3">
                {t.archivedAt && <Badge tone="amber">Archived</Badge>}
                <span className="text-xs text-faint">{t.eventCount ? `${t.eventCount} ${t.eventCount === 1 ? "event" : "events"} · last ${timeAgo(t.lastEventAt)}` : "No events yet"}</span>
                {owns && !t.archivedAt && <Switch size="sm" aria-label={`${t.name} on`} checked={t.enabled} onChange={(v) => void toggle(t, v)} />}
              </div>
            }
          />
        ))}
      </List>
      {open && (
        <TriggerDialog
          agent={agent} type={type} trigger={open === "new" ? null : open} owns={owns}
          onIssued={setIssued} onSaved={load} onClose={() => setOpen(null)}
        />
      )}
      {issued && (
        <Dialog open onClose={() => setIssued(null)} title={`${issued.trigger.name}: its address and secret`} description="Paste both into the app that sends the events." cancel={false}
          primary={<Button variant="primary" onClick={() => setIssued(null)}>Done</Button>}>
          <div className="space-y-3">
            <CopyBox label="Address" value={issued.trigger.url} />
            <CopyBox label="Signing secret" value={issued.secret} />
            <p className="text-sm text-muted">Copy the secret now: it is stored sealed and never shown again. Rotate it if it is lost.</p>
          </div>
        </Dialog>
      )}
    </>
  );
}

/** A trigger opened: its form (read only for anyone but the agent's owners), its events, and its way out. */
function TriggerDialog({ agent, type, trigger, owns, onIssued, onSaved, onClose }: {
  agent: AgentDTO; type: SubjectTypeDTO | null; trigger: TriggerDTO | null; owns: boolean;
  onIssued: (i: Issued) => void; onSaved: () => void; onClose: () => void;
}) {
  const [name, setName] = useState(trigger?.name ?? "");
  const [eventTypes, setEventTypes] = useState((trigger?.eventTypes ?? []).join("\n"));
  const [conditions, setConditions] = useState((trigger?.conditions ?? []).map((c) => ({ path: c.path, equals: showEquals(c.equals) })));
  const [scoped, setScoped] = useState<"none" | "find">(trigger?.scopeRule ? "find" : "none");
  const tools = type?.fields.map((f) => f.system) ?? [];
  const [rule, setRule] = useState({ tool: trigger?.scopeRule?.tool ?? tools[0] ?? "", path: trigger?.scopeRule?.path ?? "", namePath: trigger?.scopeRule?.namePath ?? "" });
  const [prompt, setPrompt] = useState(trigger?.prompt ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readOnly = !owns;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const body = () => ({
    name, prompt,
    eventTypes: eventTypes.split(/[\n,]/).map((t) => t.trim()).filter(Boolean),
    conditions: conditions.filter((c) => c.path.trim()).map((c) => ({ path: c.path.trim(), equals: parseEquals(c.equals) })),
    scopeRule: scoped === "find" && type ? { type: type.key, tool: rule.tool, path: rule.path.trim(), namePath: rule.namePath.trim() || null } : null,
  });
  const save = () => act(async () => {
    if (trigger) await patch(`/api/triggers/${trigger.id}`, body());
    else onIssued(await post<Issued>(`/api/agents/${agent.id}/triggers`, body()));
    onSaved();
    onClose();
  });
  const rotate = () => act(async () => {
    if (!trigger || !(await confirmDialog({ title: `Rotate the secret of ${trigger.name}?`, body: "The current secret stops working at once; the new one is shown once.", confirm: "Rotate secret", danger: true }))) return;
    onIssued(await post<Issued>(`/api/triggers/${trigger.id}/rotate-secret`));
    onSaved();
    onClose();
  });
  const archive = () => act(async () => {
    await patch(`/api/triggers/${trigger!.id}`, { archived: !trigger!.archivedAt });
    onSaved();
    onClose();
  });
  const remove = () => act(async () => {
    if (!(await confirmDialog({ title: `Delete ${trigger!.name}?`, body: "Its address stops answering for good.", confirm: "Delete trigger", danger: true }))) return;
    await del(`/api/triggers/${trigger!.id}`);
    onSaved();
    onClose();
  });

  return (
    <Dialog
      open onClose={onClose} size="lg" busy={busy}
      title={trigger ? trigger.name : "New trigger"}
      description={trigger ? (trigger.archivedAt ? "Archived: its address answers nothing." : trigger.enabled ? `On: events start runs of ${agent.name}.` : "Off: its address answers nothing.") : `Its address and secret are shown once it is saved.`}
      secondary={owns && trigger && (
        <div className="flex flex-wrap gap-2">
          <Button variant="danger" onClick={rotate}>Rotate secret</Button>
          {trigger.eventCount ? <Button onClick={archive}>{trigger.archivedAt ? "Unarchive" : "Archive"}</Button> : <Button variant="danger" onClick={remove}>Delete</Button>}
        </div>
      )}
      primary={owns ? <Button variant="primary" disabled={busy} onClick={save}>{trigger ? "Save" : "Create trigger"}</Button> : undefined}
    >
      <div className="space-y-4">
        {trigger && <CopyBox label="Address" value={trigger.url} />}
        {trigger?.secretTiedToToken && <p className="text-xs text-muted">Its secret was made in a session a token opened, so it stops working when that token does. Rotate it signed in with Google to keep it.</p>}
        <Field label="Name">
          <input className={inputCls} value={name} disabled={readOnly} onChange={(e) => setName(e.target.value)} placeholder="Replies at an account" />
        </Field>
        <Field label="Event types" hint="One per line, as the app names them; an event of any other type is ignored.">
          <textarea className={inputCls} rows={2} value={eventTypes} disabled={readOnly} onChange={(e) => setEventTypes(e.target.value)} placeholder="outreach.reply.received" />
        </Field>
        <FieldGroup label="Conditions" hint="All must hold, or the event is ignored. A value is true, false, a number or text.">
          {conditions.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={inputCls} aria-label="Path" value={c.path} disabled={readOnly} placeholder="data.automated"
                onChange={(e) => setConditions(conditions.map((x, j) => (j === i ? { ...x, path: e.target.value } : x)))} />
              <span className="text-sm text-muted">equals</span>
              <input className={inputCls} aria-label="Value" value={c.equals} disabled={readOnly} placeholder="false"
                onChange={(e) => setConditions(conditions.map((x, j) => (j === i ? { ...x, equals: e.target.value } : x)))} />
              {owns && <TextLink onClick={() => setConditions(conditions.filter((_, j) => j !== i))}>remove</TextLink>}
            </div>
          ))}
          {owns && conditions.length < 10 && <TextLink onClick={() => setConditions([...conditions, { path: "", equals: "" }])}>Add a condition</TextLink>}
        </FieldGroup>
        <FieldGroup label="Scope" hint={type ? `Find the ${type.name.toLowerCase()} by its id in one of its tools; with none linked, one is made in ${tools[0] ?? "its main tool"}.` : `${agent.name} has no scope type: its runs are at its top level.`}>
          {type && (
            <Choice label="Scope" value={scoped} onChange={readOnly ? () => {} : setScoped} disabled={readOnly}
              options={[{ value: "none", label: "No scope" }, { value: "find", label: `Find the ${type.name.toLowerCase()}` }]} />
          )}
          {type && scoped === "find" && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <select className={inputCls} aria-label="Tool" value={rule.tool} disabled={readOnly} onChange={(e) => setRule({ ...rule, tool: e.target.value })}>
                {tools.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className={inputCls} aria-label="Where the id is" value={rule.path} disabled={readOnly} placeholder="account.id" onChange={(e) => setRule({ ...rule, path: e.target.value })} />
              <input className={inputCls} aria-label="Where the name is" value={rule.namePath} disabled={readOnly} placeholder="account.name (optional)" onChange={(e) => setRule({ ...rule, namePath: e.target.value })} />
            </div>
          )}
        </FieldGroup>
        <Field label="Prompt" hint="What the run is told, the same for every event. The event itself is handed over as material.">
          <textarea className={inputCls} rows={3} maxLength={2000} value={prompt} disabled={readOnly} onChange={(e) => setPrompt(e.target.value)} />
        </Field>
        <ErrorText error={error} />
        {trigger && <TriggerEvents trigger={trigger} />}
      </div>
    </Dialog>
  );
}

/**
 * Every call the trigger received and what it did, newest first, 50 at a time. A list, not a table: inside a dialog the
 * rows must stack at any width, and each says what it is on the left and what it did on the right.
 */
function TriggerEvents({ trigger }: { trigger: TriggerDTO }) {
  const { rows, done, loading, error, more } = usePaged<TriggerEventDTO>(
    (before) => get(`/api/triggers/${trigger.id}/events?${new URLSearchParams(before ? { before } : {})}`), [trigger.id],
  );
  return (
    <>
      <List
        title="Events"
        hint="Every call to its address: started a run, a repeat of one handled before, ignored, or refused."
        empty={rows?.length === 0 ? "No event yet." : undefined}
        footer={error && <ErrorText error={error} />}
      >
        {(rows ?? []).map((e) => (
          <ListRow
            key={e.id}
            title={e.type ?? "No type"}
            subtitle={`${timeAgo(e.receivedAt)} · ${e.eventId}`}
            trailing={
              <div className="flex min-w-0 items-center gap-2 text-xs text-muted">
                <Badge tone={OUTCOME_TONE[e.outcome]}>{e.outcome}</Badge>
                <span className="min-w-0 break-words">
                  {e.runId ? <TextLink to={`/admin/runs/${e.runId}`}>run{e.scopeName ? ` on ${e.scopeName}` : ""}</TextLink> : (e.reason ?? "")}
                </span>
              </div>
            }
          />
        ))}
      </List>
      <MoreOnScroll onMore={more} done={done} loading={loading} />
    </>
  );
}
